/**
 * Writing a finding by hand: loading one, holding the edits, saving them back.
 *
 * A partial update leaves everything it did not mention alone — sending only a title must
 * never strip a finding's links — which is why the editor re-sends every field it knows
 * about and the server treats absent as "leave alone" rather than "empty it".
 *
 * The form's shape and its inputs live in `findingFields.tsx`. The seam is state: nothing
 * there touches the network or holds any, and nothing here renders a label.
 *
 * Part of the study workspace, which is separate from `cards.tsx` — that file is the *text*
 * (passages, evidence, the lexicon), and this is where a reader keeps what they found. They
 * share the output window and its stack, because a finding is a passage-shaped thing, but
 * they are different jobs. Cards stay data, never closures: everything a card needs from
 * React state arrives as a prop on the window, so the stack stays serialisable.
 */

import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import type { Account, FindingDetail } from '../account'
import { createFinding, listPacks, readFinding, updateFinding } from '../account'
import type { Card } from '../cards'
import { describeFailure } from '@shared/user-errors.js'
import { t } from '../i18n'

import type { EditorState } from './findingFields'
import {
  BLANK,
  ClassificationRow,
  DescriptorFields,
  EdgeFields,
  SlugField,
  SlugOptions,
  toPayload,
} from './findingFields'
import { Failure, Loading } from './cardDisplay'
import type { EditConflict } from './editConflict'
import { ConflictNotice, useConflict } from './editConflict'
import { FormActions, fromList } from './recordEditing'
import { asPickable } from './vocabulary'

/** What the editor form needs from the card that owns the finding. */
interface EditorSession {
  fields: EditorState | null
  set: (patch: Partial<EditorState>) => void
  failure: string | null
  busy: boolean
  published: boolean
  originalSlug: string
  save: (event: React.FormEvent) => Promise<void>
  conflict: EditConflict<EditorState> | null
  takeTheirs: () => void
  keepMine: () => void
}

/** A stored finding as the form edits it. Used on load, and for the other side of a conflict. */
function toEditorState(finding: FindingDetail, account: Account | null): EditorState {
  return {
    title: finding.title,
    status: finding.status,
    claim_type: finding.claim_type ?? '',
    // The stored value is a name; the picker offers a relation. A name
    // that is the reader's own selects "my own reading", so re-saving an
    // untouched finding cannot quietly reassign its authorship.
    origin: asPickable(finding.origin, account),
    slug: finding.slug,
    themes: fromList(finding.themes),
    keywords: fromList(finding.keywords),
    scripture: fromList(finding.scripture),
    sources: fromList(finding.sources),
    related: fromList(finding.related),
    builds_on: fromList(finding.builds_on),
    tension_with: fromList(finding.tension_with),
    body: finding.body,
  }
}

/** The form's own labels, so a conflict's comparison reads like the form it sits in. */
const FINDING_LABELS = (): Partial<Record<keyof EditorState, string>> => ({
  title: t('editor.claim'),
  status: t('editor.status'),
  claim_type: t('editor.kind'),
  origin: t('editor.origin'),
  slug: t('editor.slug'),
  themes: t('editor.themes'),
  keywords: t('editor.keywords'),
  scripture: t('editor.scripture'),
  sources: t('editor.sources'),
  body: t('editor.body'),
})

/** The edit buffer: a blank finding for a new one, or the stored one once it arrives. */
function useEditBuffer(
  id: number | null,
  account: Account | null,
): {
  fields: EditorState | null
  setFields: React.Dispatch<React.SetStateAction<EditorState | null>>
  originalSlug: string
  rowVersion: number | null
  setRowVersion: React.Dispatch<React.SetStateAction<number | null>>
  failure: string | null
  setFailure: React.Dispatch<React.SetStateAction<string | null>>
} {
  const [fields, setFields] = useState<EditorState | null>(id === null ? BLANK : null)
  const [originalSlug, setOriginalSlug] = useState('')
  // The version the form was loaded at; an update says so, and a stale one is refused.
  const [rowVersion, setRowVersion] = useState<number | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    if (id === null) return
    readFinding(id).then(
      (finding) => {
        setOriginalSlug(finding.slug)
        setRowVersion(finding.row_version)
        setFields(toEditorState(finding, account))
      },
      (error: unknown) => setFailure(describeFailure(error, t('error.notLoaded'))),
    )
    // `account` is read inside, and is deliberately not a dependency: this effect fills
    // the form, so re-running it would discard whatever the reader had typed the moment
    // the account request came back. The value it is used for — which origin the picker
    // starts on — matters only at load, and a reader who changes it has said so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return { fields, setFields, originalSlug, rowVersion, setRowVersion, failure, setFailure }
}

/**
 * Whether this finding has already gone out to subscribers.
 *
 * Changing an id that has been published breaks every edge and wikilink pointing at it —
 * including ones in somebody else's corpus, which we cannot fix for them. A failed lookup
 * reports `false` deliberately: the warning is advice, and a pack list that did not load
 * is no reason to block an edit.
 */
function usePublishedStatus(
  id: number | null,
  originalSlug: string,
  corpus: string | null,
): boolean {
  const [published, setPublished] = useState(false)

  useEffect(() => {
    if (id === null || !originalSlug) return
    listPacks(corpus ?? undefined).then(
      (list) =>
        setPublished(
          list.packs.some(
            (pack) => pack.latest_version !== null && pack.findings.includes(originalSlug),
          ),
        ),
      () => setPublished(false),
    )
  }, [id, originalSlug, corpus])

  return published
}

/**
 * Create or update. Returns the new id after a create, `null` after an update; throws
 * whatever the server refused, including a conflict, for the caller to show.
 */
async function writeFinding({
  id,
  fields,
  corpus,
  consented,
  originalSlug,
  rowVersion,
}: {
  id: number | null
  fields: EditorState
  corpus: string | null
  consented: boolean
  originalSlug: string
  rowVersion: number | null
}): Promise<number | null> {
  const payload = toPayload(fields)
  if (id === null) {
    const created = await createFinding({
      ...payload,
      corpus: corpus ?? undefined,
      slug: fields.slug || undefined,
      consent: !consented,
    })
    return created.id
  }
  await updateFinding(id, {
    ...payload,
    slug: fields.slug !== originalSlug ? fields.slug : undefined,
    // Loaded before the form could render, so never null by the time Save is pressed.
    row_version: rowVersion ?? undefined,
  })
  return null
}

/**
 * Loading a finding, and writing it back.
 *
 * Split from the card because they are two jobs with one shared value: this owns
 * *what the finding is* — the fetch, the edit buffer, the save and its failures —
 * while the card owns *how it is asked for*. The form reads `fields` and raises
 * patches; it never touches the network, so a change to either half stops being a
 * change to both.
 */
function useFindingEditor({
  id,
  corpus,
  account,
  consented,
  onOpen,
  onBack,
}: {
  id: number | null
  corpus: string | null
  account: Account | null
  consented: boolean
  onOpen: (card: Card) => void
  onBack: () => void
}): EditorSession {
  const { fields, setFields, originalSlug, rowVersion, setRowVersion, failure, setFailure } =
    useEditBuffer(id, account)
  const published = usePublishedStatus(id, originalSlug, corpus)
  const [busy, setBusy] = useState(false)
  const conflicts = useConflict(
    (finding: FindingDetail) => toEditorState(finding, account),
    setFields,
    setRowVersion,
  )

  // The functional form rather than a spread of the rendered `fields`: it is the same
  // patch either way, and this one is also correct before the fetch has landed, which is
  // what lets the hook hand `set` out above the card's loading guard.
  const set = (patch: Partial<EditorState>) =>
    setFields((current) => (current === null ? current : { ...current, ...patch }))

  async function save(event: React.FormEvent) {
    event.preventDefault()
    if (!fields) return
    if (!fields.title.trim()) return setFailure(t('editor.titleRequired'))

    setBusy(true)
    setFailure(null)
    conflicts.clear()
    try {
      const createdId = await writeFinding({ id, fields, corpus, consented, originalSlug, rowVersion })
      if (createdId === null) onBack()
      else onOpen({ kind: 'finding', id: createdId })
    } catch (error) {
      if (!conflicts.refused(error)) setFailure(error instanceof Error ? error.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  const { conflict, takeTheirs, keepMine } = conflicts
  return { fields, set, failure, busy, published, originalSlug, save, conflict, takeTheirs, keepMine }
}

export function FindingEditorCard(props: {
  id: number | null
  corpus: string | null
  account: Account | null
  consented: boolean
  onOpen: (card: Card) => void
  onBack: () => void
}): JSX.Element {
  const { id, corpus, onBack } = props
  const { fields, set, failure, busy, published, originalSlug, save, conflict, takeTheirs, keepMine } =
    useFindingEditor(props)

  if (failure && !fields) return <Failure message={failure} />
  if (!fields) return <Loading />

  return (
    <article>
      <h2 className="card__kind">{id === null ? t('editor.new') : t('editor.edit')}</h2>
      <form className="form" onSubmit={save}>
        <label>
          {t('editor.claim')}
          <textarea
            value={fields.title}
            onChange={(event) => set({ title: event.target.value })}
            rows={2}
            required
          />
          <span className="muted">{t('editor.claimHint')}</span>
        </label>

        <ClassificationRow fields={fields} set={set} />
        <DescriptorFields fields={fields} set={set} />
        <EdgeFields fields={fields} set={set} />

        <label>
          {t('editor.body')}
          <textarea
            value={fields.body}
            onChange={(event) => set({ body: event.target.value })}
            rows={12}
          />
        </label>

        <SlugField
          slug={fields.slug}
          originalSlug={originalSlug}
          published={published}
          set={set}
        />

        {failure && <Failure message={failure} />}
        {conflict && (
          <ConflictNotice
            conflict={conflict}
            mine={fields}
            labels={FINDING_LABELS()}
            onUseTheirs={takeTheirs}
            onKeepMine={keepMine}
          />
        )}

        <FormActions onCancel={onBack} busy={busy} />
      </form>

      <SlugOptions corpus={corpus} />
    </article>
  )
}
